
import { createContext, useContext } from 'react'
import type { SignalCategory } from '@/lib/signals'

/**
 * View state the nodes need but that must not live in node data — putting it
 * there would rewrite every node on each filter or edge change.
 */
export type CanvasViewState = {
  /** Signal categories currently shown. Hidden ones drop out of nodes and edges. */
  visibleSignals: Record<SignalCategory, boolean>
  /** Ports with a cable on them. These stay visible inside a collapsed run. */
  patchedPortIds: Set<string>
  /**
   * Port runs the user has explicitly opened. Long runs are collapsed by
   * default, so tracking what is *expanded* means there is no initial state to
   * compute when a node first mounts.
   */
  expandedGroups: Set<string>
  toggleGroup: (key: string) => void
  /** Runs at least this long collapse by default. */
  collapseThreshold: number
}

export const CanvasViewContext = createContext<CanvasViewState>({
  visibleSignals: { VIDEO: true, AUDIO: true, NETWORK: true, DATA: true, POWER: true },
  patchedPortIds: new Set(),
  expandedGroups: new Set(),
  toggleGroup: () => {},
  collapseThreshold: 6,
})

export function useCanvasView() {
  return useContext(CanvasViewContext)
}

/**
 * Collapse state is tracked per node, per scope ('board' for onboard ports or
 * a slot id), per direction, per run — so two identical cards in one computer
 * expand and collapse independently.
 */
export function groupKey(nodeId: string, scope: string, direction: string, group: string) {
  return `${nodeId}::${scope}::${direction}::${group}`
}
